import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Users, UserCheck, Wallet, Receipt, Cake, Smartphone, Loader2 } from "lucide-react";

const PALETTE = ["#1B4332", "#E07A5F", "#457B9D", "#F4A261", "#81B29A", "#3D405B", "#D4A373"];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  const k = data.kpis || {};
  const kpis = [
    { label: "Total Employees", value: k.total_employees, icon: Users, accent: "#1B4332", bg: "#D8F3DC" },
    { label: "Present Today", value: k.present_today, icon: UserCheck, accent: "#457B9D", bg: "#DCE9F0" },
    { label: "On Leave Today", value: k.on_leave_today, icon: UserCheck, accent: "#E07A5F", bg: "#FADED7" },
    { label: "Salary Payable", value: formatINR(k.salary_payable), icon: Wallet, accent: "#1B4332", bg: "#D8F3DC" },
    { label: "TA/DA Pending", value: k.tada_pending, icon: Receipt, accent: "#F4A261", bg: "#FCE7C9" },
    { label: "Pending Leaves", value: k.pending_leaves, icon: UserCheck, accent: "#E07A5F", bg: "#FADED7" },
    { label: "Mobile Assigned", value: k.mobile_assigned, icon: Smartphone, accent: "#3D405B", bg: "#E5E6EE" },
    { label: "Total Assets", value: k.total_assets, icon: Smartphone, accent: "#81B29A", bg: "#E1EFE3" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Management Dashboard</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Good day at Navaratnaa.</h1>
          <p className="text-stone-600 mt-2 text-sm">A live pulse on workforce, payroll, attendance & field expenses.</p>
        </div>
        <div className="text-sm text-stone-500">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      </header>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        {kpis.map((k) => (
          <div key={k.label} className="card-flat" data-testid={`kpi-${k.label.toLowerCase().replace(/[\s/]/g, "-")}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="kpi-label">{k.label}</div>
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: k.bg }}>
                <k.icon className="w-4 h-4" style={{ color: k.accent }} />
              </div>
            </div>
            <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: k.accent }}>
              {k.value ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-flat lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="kpi-label">Trend · last 14 days</div>
              <h3 className="font-heading text-xl font-bold mt-1">Daily Attendance</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={data.attendance_trend || []}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1B4332" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#1B4332" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#57534E" }}
                       tickFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} />
                <Tooltip />
                <Area type="monotone" dataKey="present" stroke="#1B4332" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat">
          <div className="kpi-label">Workforce</div>
          <h3 className="font-heading text-xl font-bold mt-1 mb-3">By Department</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.dept_distribution || []} dataKey="count" nameKey="department"
                     cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2}>
                  {(data.dept_distribution || []).map((d) => (
                    <Cell key={d.department} fill={PALETTE[(data.dept_distribution || []).indexOf(d) % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-flat lg:col-span-2">
          <div className="kpi-label">Payroll</div>
          <h3 className="font-heading text-xl font-bold mt-1 mb-3">Monthly Salary Cost</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={data.salary_trend || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#57534E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                <Tooltip formatter={(v) => formatINR(v)} />
                <Bar dataKey="total" fill="#E07A5F" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat">
          <div className="flex items-center gap-2 mb-1">
            <Cake className="w-4 h-4" style={{ color: "var(--color-secondary)" }} />
            <div className="kpi-label">Celebrations</div>
          </div>
          <h3 className="font-heading text-xl font-bold mb-4">Upcoming Birthdays</h3>
          <div className="space-y-3 max-h-56 overflow-y-auto" data-testid="birthdays-list">
            {(data.upcoming_birthdays || []).length === 0 && <div className="text-sm text-stone-500">No birthdays in next 30 days</div>}
            {(data.upcoming_birthdays || []).map((b) => (
              <div key={b.id} className="flex items-center gap-3">
                <img src={b.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.full_name}</div>
                  <div className="text-xs text-stone-500">{b.department_name}</div>
                </div>
                <div className="text-xs font-semibold px-2 py-1 rounded" style={{ background: "#FADED7", color: "#9A3412" }}>
                  {b.days_to === 0 ? "Today" : `in ${b.days_to}d`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
